import { ERROR_404_ROUTE } from '../../constants'
import { logMessage } from '../error/logger'

interface IRoutes {
  tsPathname: string
  hfIntelligenceRouteKey: string
}

interface IRouteSegments {
  baseTsPath: string
  primaryGuid: string
  remainingSegments: string[]
  isInvalidRoute: boolean
}

const ROUTE_KEY_TYPES = {
  Dashboard: 'dashboard',
  Dashboards: 'dashboards',
  Visualisation: 'visualisation',
  Visualisations: 'visualisations',
  Home: 'home',
  CreateAVisual: 'create-visual',
  AskAI: 'ask-ai',
} as const

/**
 * /embed/viz and /embed/insights viz are mapped to /insights/pinboard in TS.
 * When the user navigates back from the Dashboard page, the route change within the TS points to either /emed/viz or /embed/insights/viz.
 * But when user navigates to the Dashboard page from Dashboards, the route change within the TS points to /insights/pinboard.
 *
 * /embed/viz is used for liveboards without tabs and /embed/insights/viz is used for liveboards with tabs.
 */

const ROUTE_MAPPINGS_CONFIG: IRoutes[] = [
  {
    tsPathname: '/insights/pinboard',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Dashboard,
  },
  {
    tsPathname: '/pinboard',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Dashboard,
  },
  {
    tsPathname: '/embed/viz',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Dashboards,
  },
  {
    tsPathname: '/embed/insights/viz',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Dashboards,
  },
  {
    tsPathname: '/insights/saved-answer',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Visualisation,
  },
  {
    tsPathname: '/embed/insights/saved-answer',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Visualisation,
  },
  {
    tsPathname: '/saved-answer',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Visualisation,
  },
]

const ROUTE_EXACT_MATCHES: IRoutes[] = [
  {
    tsPathname: '/insights/home/liveboards',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Dashboards,
  },
  {
    tsPathname: '/insights/home/answers',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Visualisations,
  },
  {
    tsPathname: '/home',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.Home,
  },
  {
    tsPathname: '/answer/',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.CreateAVisual,
  },
  {
    tsPathname: '/insights/answer',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.CreateAVisual,
  },
  {
    tsPathname: '/embed/insights/conv-assist',
    hfIntelligenceRouteKey: ROUTE_KEY_TYPES.AskAI,
  } 
]

/**
 * The following routes are ignored and not mapped to any HfIntelligence URL.
 * This is because we donot have a component from TS embed SDK to handle these routes.
 * If an item matches this route, convertTsUrlToHfIntelligenceUrl will return an empty string.
 * Navigating with empty string will not change the URL and the user will stay on the same page.
 * The /insights/eureka is the redirection to the AI Visualisation from the Ask AI in home page.
 * The /insights/conv-assist is the redirection to the Ask AI page.
 * The /insights/ai-answer is the redirection to the the Edit Visualisation page from AI Visualisation.
 */
export const IGNORE_ROUTE_MAPPINGS = [
  '/insights/eureka',
  '/insights/ai-answer',
  '/insights/conv-assist',
]

const ROUTE_MAPPINGS = new Map<string, string>()
ROUTE_MAPPINGS_CONFIG.forEach((route) => {
  ROUTE_MAPPINGS.set(route.tsPathname, route.hfIntelligenceRouteKey)
})

const ROUTE_EXACT_MATCHES_MAPPINGS = new Map<string, string>()
ROUTE_EXACT_MATCHES.forEach((route) => {
  ROUTE_EXACT_MATCHES_MAPPINGS.set(route.tsPathname, route.hfIntelligenceRouteKey)
})

// Matches the parts of a ThoughtSpot URL for base path, GUID of primary items like (dashboard, visualisation), and remaining segments.
const TS_URL_SEGMENT_REGEX =
  /^(.*?)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(.*)/i

/**
 * For Subscriptions, TS redirects to its Liveboard page with an modal open.
 * Currently, TS doesn't provide a way to open the modal directly.
 * So we need to check if the URL contains the sequence GUIDs for the Liveboard and the kpi.
 * Regex to check for the sequence GUIDs:
 * For modals, the URL contains two GUIDs separated by a slash.
 * The first GUID is the Liveboard GUID and the second GUID is the kpi GUID.
 */

const SEQUENCE_GUIDS_REGEX =
  /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i

/**
 * Resolves the HfIntelligence URL for a given ThoughtSpot URL.
 * @param route The ThoughtSpot URL to resolve.
 * @returns The corresponding HfIntelligence URL, or an invalidRoute string if no match is found.
 */
const convertTsUrlToHfIntelligenceUrl = (route: string): string => {
  if (IGNORE_ROUTE_MAPPINGS.includes(route)) {
    return ''
  }

  const matchedHfIntelligenceExactRouteKey = ROUTE_EXACT_MATCHES_MAPPINGS.get(route)
  if (matchedHfIntelligenceExactRouteKey) {
    return constructHfIntelligenceUrl(matchedHfIntelligenceExactRouteKey)
  }

  return resolveUrl(route)
}

const resolveUrl = (route: string) => {
  const routeParts = extractRouteSegments(route)
  if (routeParts.isInvalidRoute) {
    logMessage(`Invalid route: ${route}`)
    return ERROR_404_ROUTE
  }
  const { baseTsPath, primaryGuid, remainingSegments } = routeParts
  const hfIntelligenceRouteKey = ROUTE_MAPPINGS.get(baseTsPath)

  if (IGNORE_ROUTE_MAPPINGS.includes(baseTsPath)) {
    return ''
  }

  if (!hfIntelligenceRouteKey) {
    logMessage(`Route not found in route mappings: ${baseTsPath}`)
    return ERROR_404_ROUTE
  }

  const hfIntelligenceRouteUrl = constructHfIntelligenceUrl(
    hfIntelligenceRouteKey,
    primaryGuid,
    remainingSegments,
    route,
  )

  return hfIntelligenceRouteUrl
}

/**
 * Extracts parts of a ThoughtSpot URL, including the base path, GUID, and remaining segments.
 * @param path
 * @returns An object containing the base path, Primary GUID, and remaining segments, or null if the URL is invalid.
 */
const extractRouteSegments = (path: string): IRouteSegments => {
  const isValidPath = validatePath(path)

  if (!isValidPath) {
    logMessage(`Invalid path: ${path}`)
    return createInvalidRoute()
  }

  const match = TS_URL_SEGMENT_REGEX.exec(path)
  if (!match) {
    logMessage(`No match found for path: ${path}`)
    return createInvalidRoute()
  }
  const [, baseTsPath, primaryGuid, ...rest] = match

  const remainingSegments = filterEmptySegments(rest)

  return { baseTsPath, primaryGuid, remainingSegments, isInvalidRoute: false }
}

/**
 * Builds the HfIntelligence route based on the route type and additional path segments.
 * @param hfIntelligenceRouteKey The type of route (e.g., 'dashboard').
 * @param route The additional path segments to append.
 * @param primaryGuid The primary GUID extracted from the ThoughtSpot URL.
 * @param remainingSegments The remaining segments of the URL.
 * @returns The constructed HfIntelligence route.
 */
const constructHfIntelligenceUrl = (
  hfIntelligenceRouteKey: string | undefined,
  primaryGuid?: string,
  remainingSegments?: string[],
  route?: string,
) => {
  switch (hfIntelligenceRouteKey) {
    case ROUTE_KEY_TYPES.Dashboard:
      return resolveDashboardUrl(primaryGuid, remainingSegments, route)

    case ROUTE_KEY_TYPES.Dashboards:
      return '/dashboards'

    case ROUTE_KEY_TYPES.Visualisations:
      return '/visualisations'

    case ROUTE_KEY_TYPES.Visualisation:
      return `/visualisations/visualisation/${primaryGuid}`

    case ROUTE_KEY_TYPES.CreateAVisual:
      return '/create-visual'

    case ROUTE_KEY_TYPES.Home:
      return '/'
    
    case ROUTE_KEY_TYPES.AskAI:
      return '/ask-ai'

    default:
      return ERROR_404_ROUTE
  }
}

const filterEmptySegments = (remainingSegments: string[]): string[] => {
  if (remainingSegments.length > 0 && remainingSegments[0] === '') {
    return []
  }

  return remainingSegments
}

const validatePath = (path: string): boolean => {
  if (!path || typeof path !== 'string') {
    return false
  }

  if (path.trim() === '') {
    return false
  }

  return true
}

const createInvalidRoute = () => ({
  baseTsPath: '',
  primaryGuid: '',
  remainingSegments: [],
  isInvalidRoute: true,
})

const resolveDashboardUrl = (primaryGuid?: string, remainingSegments?: string[], route?: string) => {
  const hasSequenceGuids = SEQUENCE_GUIDS_REGEX.test(route ?? '')
  if (hasSequenceGuids && remainingSegments) {
    return `/kpi/${primaryGuid}${remainingSegments[0]}`
  }

  return `/dashboards/dashboard/${primaryGuid}`
}

export { convertTsUrlToHfIntelligenceUrl, extractRouteSegments }
